import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import Button from '../components/Button';
import { useSupportRequest } from '../hooks/useSupport';
import { FaRegCopy } from 'react-icons/fa';
import {
  FiUser,
  FiCreditCard,
  FiMonitor,
  FiAlertTriangle,
  FiHelpCircle,
  FiCheck,
  FiArrowLeft,
  FiBookOpen,
  FiPhone,
} from 'react-icons/fi';

export const Route = createFileRoute('/_auth/contact-support')({
  component: RouteComponent,
});

const topics = [
  { id: 'account', label: 'Account', description: 'Login, settings, profile', icon: FiUser },
  { id: 'billing', label: 'Billing', description: 'Payments, subscription, invoices', icon: FiCreditCard },
  { id: 'technical', label: 'Technical', description: 'Bugs, errors, loading issues', icon: FiMonitor },
  { id: 'suspicious', label: 'Something suspicious', description: 'Scam concerns, safety questions', icon: FiAlertTriangle },
  { id: 'content', label: 'Training content', description: 'Questions, corrections, or suggestions', icon: FiBookOpen },
  { id: 'other', label: 'Something else', description: 'General questions or feedback', icon: FiHelpCircle },
] as const;

type TopicId = (typeof topics)[number]['id'];

function RouteComponent() {
  const { user: clerkUser } = useUser();
  const userEmail = clerkUser?.primaryEmailAddress?.emailAddress || '';
  const userName = clerkUser?.firstName || clerkUser?.fullName || '';

  // Pre-fill from URL params (e.g. from Emergency Wizard or Situation Assessment)
  const urlParams = new URLSearchParams(window.location.search);
  const prefillTopic = urlParams.get('topic');
  const prefillMessage = urlParams.get('message');

  const [selectedTopic, setSelectedTopic] = useState<TopicId | null>(() => {
    if (prefillTopic && topics.some((t) => t.id === prefillTopic)) return prefillTopic as TopicId;
    return null;
  });
  const [message, setMessage] = useState(prefillMessage || '');
  const [phone, setPhone] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [copied, setCopied] = useState(false);

  const supportRequestMutation = useSupportRequest();
  const referenceId = supportRequestMutation.data?.referenceId;
  const isLoading = supportRequestMutation.status === 'pending';

  const selectedTopicLabel = topics.find((t) => t.id === selectedTopic)?.label || '';

  const handleCopyReferenceId = async () => {
    if (!referenceId) return;
    try {
      await navigator.clipboard.writeText(referenceId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // no-op
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTopic || !message.trim()) return;
    supportRequestMutation.mutate(
      {
        email: userEmail,
        subject: `[${selectedTopicLabel}] Support request`,
        description: message,
        phone: phone.trim() || undefined,
        category: selectedTopic,
      },
      { onSuccess: () => setSubmitted(true) },
    );
  };

  const handleReset = () => {
    setSelectedTopic(null);
    setMessage('');
    setPhone('');
    setSubmitted(false);
    supportRequestMutation.reset();
  };

  // ── Success state ──────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="min-h-screen bg-[#f5f5f7]">
        <div className="mx-auto max-w-xl px-6 pb-16 pt-10 sm:px-8 sm:pt-14">
          <div className="rounded-2xl bg-white p-8 text-center shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)] sm:p-10">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50">
              <FiCheck className="h-7 w-7 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-semibold text-slate-900">Message sent</h1>
            <p className="mx-auto mt-2 max-w-sm text-base leading-relaxed text-slate-500">
              We've received your request and will get back to you at <span className="font-medium text-slate-700">{userEmail}</span> as soon as we can.
            </p>

            {referenceId && (
              <div className="mx-auto mt-5 inline-flex items-center gap-2 rounded-xl bg-slate-50 px-4 py-2.5">
                <span className="text-sm text-slate-400">Reference:</span>
                <span className="text-sm font-medium text-slate-700">{referenceId}</span>
                <button
                  type="button"
                  onClick={handleCopyReferenceId}
                  className="ml-1 cursor-pointer text-slate-400 transition-colors hover:text-slate-700"
                  aria-label="Copy Reference ID"
                  title={copied ? 'Copied!' : 'Copy to clipboard'}
                >
                  {copied ? (
                    <FiCheck className="h-3.5 w-3.5 text-emerald-500" />
                  ) : (
                    <FaRegCopy className="h-3.5 w-3.5" />
                  )}
                </button>
              </div>
            )}

            <div className="mt-6 flex items-center justify-center gap-4">
              <a
                href="/messages"
                className="text-sm font-medium text-violet-700 transition-colors hover:text-violet-800"
              >
                View messages
              </a>
              <span className="text-slate-300">&middot;</span>
              <button
                type="button"
                onClick={handleReset}
                className="cursor-pointer text-sm font-medium text-slate-500 transition-colors hover:text-slate-900"
              >
                Send another message
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Form state ─────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#f5f5f7]">
      <div className="mx-auto max-w-4xl px-6 pb-16 pt-10 sm:px-8 sm:pt-14">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
            Contact support
          </h1>
          <p className="mt-2 text-lg leading-relaxed text-slate-500">
            Tell us what's going on and we'll get back to you as soon as we can.
          </p>
        </div>

        <div className="rounded-2xl bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)]">
          {/* Sending as bar */}
          <div className="flex items-center gap-2 border-b border-slate-100 px-6 py-4 sm:px-8">
            <span className="text-sm text-slate-400">Sending as</span>
            <span className="text-sm font-medium text-slate-700">
              {userName ? `${userName} (${userEmail})` : userEmail}
            </span>
          </div>

          <div className="p-6 sm:p-8">
            {/* Step 1: Topic selection */}
            <div className="mb-8">
              <p className="mb-3 text-base font-medium text-slate-700">What do you need help with?</p>
              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
                {topics.map((topic) => {
                  const Icon = topic.icon;
                  const isActive = selectedTopic === topic.id;
                  return (
                    <button
                      key={topic.id}
                      type="button"
                      onClick={() => setSelectedTopic(topic.id)}
                      className={`group flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3.5 text-left transition-all ${
                        isActive
                          ? 'border-violet-400 bg-violet-50'
                          : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <Icon className={`mt-[3px] h-5 w-5 shrink-0 ${isActive ? 'text-violet-500' : 'text-slate-400'}`} />
                      <div>
                        <span className="text-sm font-medium text-slate-800 leading-5">{topic.label}</span>
                        <span className="mt-0.5 block text-sm leading-snug text-slate-400">
                          {topic.description}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Step 2: Message */}
            <form onSubmit={handleSubmit}>
              <div className="mb-5">
                <label htmlFor="support-message" className="mb-2 block text-base font-medium text-slate-700">
                  Your message
                </label>
                {prefillMessage && (
                  <p className="mb-2 text-sm text-slate-400">
                    We've included some context from your previous activity. Feel free to edit or add more details.
                  </p>
                )}
                <textarea
                  id="support-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Describe what's happening or what you need help with..."
                  required
                  className="block w-full resize-none rounded-xl border border-slate-200 bg-white px-4 py-3 text-base text-slate-900 placeholder-slate-400 outline-none transition-colors focus:border-violet-400 focus:ring-2 focus:ring-violet-100 min-h-[140px]"
                />
              </div>

              {/* Optional callback number */}
              <div className="mb-5">
                <label htmlFor="support-phone" className="mb-2 flex items-center gap-1.5 text-base font-medium text-slate-700">
                  <FiPhone className="h-4 w-4 text-slate-400" />
                  Phone number
                  <span className="text-sm font-normal text-slate-400">(optional)</span>
                </label>
                <input
                  id="support-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="If you'd like us to call you back"
                  className="block w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-base text-slate-900 placeholder-slate-400 outline-none transition-colors focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
                />
              </div>

              {supportRequestMutation.isError && (
                <div className="mb-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">
                  {supportRequestMutation.error?.message || 'Something went wrong. Please try again.'}
                </div>
              )}

              <div className="flex items-center justify-between gap-4">
                <a
                  href="/help"
                  className="inline-flex items-center gap-1.5 text-sm text-slate-400 transition-colors hover:text-slate-700"
                >
                  <FiArrowLeft className="h-3.5 w-3.5" />
                  Back to Help
                </a>
                <Button
                  type="submit"
                  variant="primary"
                  size="large"
                  isLoading={isLoading}
                  disabled={isLoading || !selectedTopic || !message.trim()}
                  className="rounded-xl"
                >
                  Send message
                </Button>
              </div>
            </form>
          </div>
        </div>

        {/* Response time note */}
        <p className="mt-4 text-center text-sm text-slate-400">
          We typically respond within 24 hours.
        </p>
      </div>
    </div>
  );
}
